<template>
  <div v-show="isVisible">
    <div
      class="modal fade"
      :class="{ show: isVisible }"
      tabindex="-1"
      role="dialog"
      :aria-hidden="!isVisible"
      :style="modalStyle"
      v-bind="passthroughAttrs"
      @click.self="handleBackdropClick"
      @keydown.esc="handleEscape"
    >
      <div
        class="modal-dialog"
        :class="dialogClasses"
        role="document"
      >
        <div class="modal-content">
          <div v-if="!hideHeader" class="modal-header">
            <h5 class="modal-title">
              <slot name="modal-title">{{ title }}</slot>
            </h5>
            <button
              type="button"
              class="close"
              aria-label="Close"
              @click="handleCancel"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <slot />
          </div>

          <div v-if="!hideFooter" class="modal-footer">
            <button
              v-if="!okOnly"
              type="button"
              class="btn"
              :class="cancelButtonClass"
              @click="handleCancel"
            >
              {{ cancelTitle }}
            </button>
            <button
              type="button"
              class="btn"
              :class="okButtonClass"
              :disabled="okDisabled"
              @click="handleOk"
            >
              {{ okTitle }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal-backdrop fade"
      :class="{ show: isVisible }"
    ></div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'HdModal',

  inheritAttrs: false,

  props: {
    value: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: Boolean,
      default: undefined
    },
    title: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: null,
      validator: (value) => value == null || ['sm', 'lg', 'xl'].includes(value)
    },
    okVariant: {
      type: String,
      default: 'primary'
    },
    cancelVariant: {
      type: String,
      default: 'secondary'
    },
    okTitle: {
      type: String,
      default: 'OK'
    },
    cancelTitle: {
      type: String,
      default: 'Cancel'
    },
    okOnly: {
      type: Boolean,
      default: false
    },
    okDisabled: {
      type: Boolean,
      default: false
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    hideFooter: {
      type: Boolean,
      default: false
    }
  },

  emits: ['input', 'update:modelValue', 'ok', 'cancel', 'hidden', 'shown'],

  data() {
    return {
      // Seeded from the prop rather than hardcoded false: a modal can be MOUNTED
      // already open — one inside a v-if branch that only renders once there is
      // something to say (a failed render's explanation) is born with its value
      // true, and the watcher below never fires because nothing changed. It
      // showed as a modal that silently refused to appear.
      isVisible: this.modelValue !== undefined ? this.modelValue : this.value
    };
  },

  computed: {
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },

    dialogClasses() {
      if (!this.size) return null;

      return `modal-${this.size}`;
    },

    okButtonClass() {
      return `btn-${this.okVariant}`;
    },

    cancelButtonClass() {
      return `btn-${this.cancelVariant}`;
    },

    modalStyle() {
      return this.isVisible ? { display: 'block' } : null;
    },

    passthroughAttrs() {
      const { class: _class, ...attrs } = this.$attrs;

      return attrs;
    }
  },

  watch: {
    currentValue(visible) {
      if (visible === this.isVisible) return;

      this.isVisible = visible;
      this.toggleBodyScroll(visible);

      if (visible) {
        this.$emit('shown');
      } else {
        this.$emit('hidden');
      }
    }
  },

  mounted() {
    // Born open (see data): the scroll lock is normally taken in the watcher,
    // which did not run.
    if (this.isVisible) this.toggleBodyScroll(true);
  },

  beforeUnmount() {
    this.toggleBodyScroll(false);
  },

  methods: {
    createModalEvent() {
      const event = {
        defaultPrevented: false,
        preventDefault() {
          this.defaultPrevented = true;
        }
      };

      return event;
    },

    show() {
      if (this.isVisible) return;

      this.isVisible = true;
      this.toggleBodyScroll(true);
      this.syncVisibility(true);
      this.$emit('shown');
    },

    hide(emitHidden = true) {
      if (!this.isVisible) return;

      this.isVisible = false;
      this.toggleBodyScroll(false);
      this.syncVisibility(false);

      if (emitHidden) this.$emit('hidden');
    },

    syncVisibility(visible) {
      this.$emit('input', visible);
      this.$emit('update:modelValue', visible);
    },

    toggleBodyScroll(lock) {
      document.body.classList.toggle('modal-open', lock);
    },

    handleOk() {
      const event = this.createModalEvent();
      this.$emit('ok', event);

      if (!event.defaultPrevented) this.hide();
    },

    handleCancel() {
      const event = this.createModalEvent();
      this.$emit('cancel', event);

      if (!event.defaultPrevented) this.hide();
    },

    handleBackdropClick() {
      this.handleCancel();
    },

    handleEscape() {
      this.handleCancel();
    }
  }
});
</script>
